import type {ReactElement} from 'react'
import {Dialog, Transition} from '@headlessui/react'
import {Fragment, useRef, useEffect} from 'react'
import React from 'react'
import {VerificationData} from '@/types'
import {useMutation} from 'react-query'
import {ResendVerification, ResendWhatsappVerification} from '@/services/api/auth'
import toast from 'react-hot-toast'
import CustomToast from '../CustomToast'
import { useLoginModal } from '@/contexts/LoginModalContext'
import LogInView from './loginModal/LogInView'
import RegisterView from './loginModal/RegisterView'
import ForgotView from './loginModal/ForgotView'
import ConvertView from './loginModal/ConvertView'
import { Trans, useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import GuestView from './loginModal/GuestView'
import { event } from 'nextjs-google-analytics'
import useAuth from '@/hooks/useAuth'

export default function LoginModal(): ReactElement {
  const {
    isOpen,
    setIsOpen,
    currentView,
    setCurrentView,
    postLoginCallback,
    setPostLoginCallback,
    productType,
    setProductType,
    executePostLoginCallback,
    location,
    name
  } = useLoginModal();

  const {locale} = useRouter();
  const userLocale = locale === 'ar' ? 'ar' : 'en'
  const { user } = useAuth('none')

  const closeModal = () => {
    setIsOpen(false)
    setCurrentView('login');
    setProductType(null);
    setPostLoginCallback(undefined);
  }

  useEffect(() => {
    if (isOpen && !currentView) {
      setCurrentView('login');
    }
  }, [isOpen]);

  
  const [verificationError, setVerificationError] = React.useState(false)
  const [verificationData, setVerificationData] = React.useState<
    undefined | VerificationData
  >()
  const [shouldVerify, setShouldVerify] = React.useState(false)

  const ResendMutation = useMutation(ResendVerification)
  const ResendWhatsappMutation = useMutation(ResendWhatsappVerification)

  const handleVerificationWhatsapp = () => {
    const phone = verificationData?.phone
    if (verificationData && phone) {
      ResendWhatsappMutation.mutate(
        {
          country_code: phone.country_code,
          number: phone.number,
          locale: userLocale
        },
        {
          onSuccess() {
            toast.custom((tel) => <CustomToast t={tel} message={tAlerts('validation_code')} type="success" />, {
              duration: 2500,
            });
          },
          onError(error: any) {
            toast.custom((t) => <CustomToast t={t} message={error?.response?.data?.message} type="error" />, {
              duration: 4000,
              id: 'Validation Error',
            });
          },
        },
      )
    }
  }

  const handleVerification = () => {
    const phone = verificationData?.phone
    if (verificationData && phone) {
      ResendMutation.mutate(
        {
          country_code: phone.country_code,
          number: phone.number,
        },
        {
          onSuccess() {
            toast.custom((tel) => <CustomToast t={tel} message={tAlerts('validation_code')} type="success" />, {
              duration: 2500,
            });
          },
          onError(error: any) {
            toast.custom((t) => <CustomToast t={t} message={error?.response?.data?.message} type="error" />, {
              duration: 4000,
              id: 'Validation Error',
            });
          },
        },
      )
    }
  }

  const [headingNumber, setHeadingNumber] = React.useState(productType ? productType : null)

  React.useEffect(()=>{
    setHeadingNumber(productType ? productType : null)
  }, [productType])
  
  // Create a ref for the initial focus element
  const initialFocusRef = useRef<HTMLButtonElement>(null);
  
  // Prevent event propagation for button clicks
  const handleButtonClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    e.preventDefault();
    action();
  }

  const {t: tCommon} = useTranslation('common');
  const {t: tAlerts} = useTranslation('alerts');

  const [showButton, setShowButton] = React.useState(true);
  useEffect(() => {
    if (isOpen && currentView === 'register' && !user) {
      event('signup_modal_open')
    }
  }, [isOpen, currentView, user])

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog 
        as="div" 
        className="relative z-[200] login-modal-dialog"
        id='login-modal-dialog' 
        onClose={closeModal}
        initialFocus={initialFocusRef} // Set initial focus
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className='fixed z-[190] inset-0 bg-black bg-opacity-25'>
          <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95">
              <Dialog.Panel 
                className="w-full max-w-md xl:max-w-2xl transform overflow-hidden rounded-2xl bg-white main-modal-area text-left align-middle shadow-xl transition-all"
                onClick={(e) => e.stopPropagation()} // Prevent click events from bubbling up
              >
                <Dialog.Title className="text-lg font-medium p-3 lg:px-4 lg:pt-4 lg:pb-2">
                  <div className="flex items-start justify-end w-full" role='dialog'>
                    {showButton && (
                      <button
                      ref={initialFocusRef} // Set as initial focus
                      onClick={e => handleButtonClick(e, closeModal)}
                      className="close-icon border bg-gray-200 rounded-md p-2 self-end cursor-pointer text-primary-500">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 21 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M11.9109 10.2326L20.201 1.94261C20.3648 1.75131 20.4504 1.50523 20.4407 1.25355C20.4309 1.00188 20.3266 0.763139 20.1485 0.585044C19.9704 0.40695 19.7317 0.302617 19.48 0.292896C19.2283 0.283175 18.9823 0.368781 18.791 0.532608L10.5009 8.82261L2.21095 0.522607C2.02265 0.334304 1.76725 0.228516 1.50095 0.228516C1.23465 0.228516 0.979253 0.334304 0.79095 0.522607C0.602646 0.710911 0.496858 0.966306 0.496858 1.23261C0.496858 1.49891 0.602646 1.7543 0.79095 1.94261L9.09095 10.2326L0.79095 18.5226C0.686268 18.6123 0.601248 18.7226 0.541224 18.8466C0.481201 18.9707 0.447471 19.1058 0.442151 19.2436C0.436832 19.3813 0.460037 19.5186 0.510312 19.6469C0.560587 19.7753 0.636845 19.8918 0.7343 19.9893C0.831754 20.0867 0.948302 20.163 1.07663 20.2132C1.20495 20.2635 1.34228 20.2867 1.48 20.2814C1.61772 20.2761 1.75285 20.2424 1.87692 20.1823C2.00098 20.1223 2.1113 20.0373 2.20095 19.9326L10.5009 11.6426L18.791 19.9326C18.9823 20.0964 19.2283 20.182 19.48 20.1723C19.7317 20.1626 19.9704 20.0583 20.1485 19.8802C20.3266 19.7021 20.4309 19.4633 20.4407 19.2117C20.4504 18.96 20.3648 18.7139 20.201 18.5226L11.9109 10.2326Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                    )}
                  </div>
                </Dialog.Title>
                {((currentView !== 'forgot' && currentView !== 'convert') && shouldVerify === false) &&
                (
                  <>
                  <div className='flex items-center justify-center gap-4 px-8 py-2' dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
                  {location === 'story' && (
                    <button 
                    className={`w-[33%] rounded-2xl 
                      flex items-center justify-center
                      gap-2 
                      lg:gap-2
                      px-2 py-3 
                      text-primary text-sm 
                      bg-success/80 
                      cursor-pointer font-bold 
                      shadow-[0_10px_15px_-3px_rgba(12,223,191,0.30),0_4px_6px_-4px_rgba(12,223,191,0.30)] 
                      ${currentView === 'guest' ? 'opacity-100 login-view-active' : 'opacity-30'}`}  
                    onClick={(e) => handleButtonClick(e, () => {
                      setCurrentView('guest')
                      setHeadingNumber(null)
                    })}
                    >
                      <span className={`${currentView === 'guest' ? 'text-primary' : 'text-[#3E627F]'} flex-shrink-0 flex items-center justify-center`}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.6666 14V12.6667C10.6666 11.9594 10.3856 11.2811 9.88554 10.781C9.38544 10.281 8.70716 10 7.99992 10H3.99992C3.29267 10 2.6144 10.281 2.1143 10.781C1.6142 11.2811 1.33325 11.9594 1.33325 12.6667V14" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M5.99992 7.33333C7.47268 7.33333 8.66659 6.13943 8.66659 4.66667C8.66659 3.19391 7.47268 2 5.99992 2C4.52716 2 3.33325 3.19391 3.33325 4.66667C3.33325 6.13943 4.52716 7.33333 5.99992 7.33333Z" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M10.6667 7.33333L12.0001 8.66667L14.6667 6" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      </span>
                      <p className="min-h-[2.5rem] lg:min-h-[1rem] leading-tight text-center line-clamp-2 lg:line-clamp-1">
                        {tCommon('guest')}
                      </p>
                    </button>
                  )}
                  <button 
                    className={`register-view-button ${location === 'story' ? 'w-[33%]':'w-[53%] lg:w-[45%]'} rounded-2xl flex items-center justify-center gap-2 px-2 py-3 text-primary text-sm bg-success/80 cursor-pointer font-bold shadow-[0_10px_15px_-3px_rgba(12,223,191,0.30),0_4px_6px_-4px_rgba(12,223,191,0.30)] ${currentView === 'register' ? 'opacity-100': 'opacity-30'}`} 
                    onClick={(e) => handleButtonClick(e, () => {
                      setCurrentView('register')
                      setHeadingNumber('3')
                    })}
                  >
                    <span className={`${currentView === 'register' ? 'text-primary' : 'text-[#3E627F]'}  flex-shrink-0 flex items-center justify-center`}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.1265 13.6979V12.4321C10.1265 11.7606 9.85973 11.1167 9.38495 10.6419C8.91017 10.1671 8.26623 9.90039 7.59479 9.90039H3.79729C3.12585 9.90039 2.48191 10.1671 2.00713 10.6419C1.53235 11.1167 1.26563 11.7606 1.26562 12.4321V13.6979" stroke="currentColor" stroke-width="1.26583" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M5.6958 7.36719C7.09377 7.36719 8.22705 6.23391 8.22705 4.83594C8.22705 3.43797 7.09377 2.30469 5.6958 2.30469C4.29783 2.30469 3.16455 3.43797 3.16455 4.83594C3.16455 6.23391 4.29783 7.36719 5.6958 7.36719Z" stroke="currentColor" stroke-width="1.26583" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M12.0254 5.46973V9.2666" stroke="currentColor" stroke-width="1.26583" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M13.9233 7.36816H10.1265" stroke="currentColor" stroke-width="1.26583" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </span>
                    <p className={`text-center ${location === 'story' ? ' line-clamp-2 lg:line-clamp-1 min-h-[2.5rem] lg:min-h-[1rem] leading-tight' : ''}`}>
                      {tCommon('register')}
                    </p>
                  </button>
                  <button 
                  className={`${location === 'story' ? 'w-[33%]':'w-[50%] lg:w-[45%]'} rounded-2xl 
                    flex items-center justify-center 
                    gap-2
                    px-2 py-3 
                    text-primary text-sm 
                    bg-success/80 
                    cursor-pointer font-bold 
                    shadow-[0_10px_15px_-3px_rgba(12,223,191,0.30),0_4px_6px_-4px_rgba(12,223,191,0.30)] 
                    ${currentView === 'login' ? 'opacity-100 login-view-active' : 'opacity-30'}`} 
                  onClick={(e) => handleButtonClick(e, () => {
                    setCurrentView('login')
                    setHeadingNumber(null)
                  })}
                  >
                    <span className={`${currentView === 'register' ? 'text-primary' : 'text-[#3E627F]'}  flex-shrink-0 flex items-center justify-center`}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 2H12.6667C13.0203 2 13.3594 2.14048 13.6095 2.39052C13.8595 2.64057 14 2.97971 14 3.33333V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H10" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M6.6665 11.3337L9.99984 8.00033L6.6665 4.66699" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M10 8H2" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </span>
                    {tCommon('login')}
                  </button>
                </div>
                  {(currentView === 'guest' && name != '') ? 
                  (
                    <h1 
                    className='w-full text-center p-2 text-primary text-xl lg:text-[42px] lg:leading-[50px] font-bold lg:my-4 mb-2'
                    >
                      <p
                      dir={locale === 'ar' ? 'rtl' : 'ltr'} 
                      className="block mx-auto max-w-[400px] lg:max-w-[500px]"
                      >
                        <Trans
                          i18nKey="guestNameHeading"
                          values={{ name }}
                          components={{
                            name: (
                              <span className="text-[#0CDFBF]">
                                <bdi />
                              </span>
                            )
                          }}
                        />
                      </p>
                    </h1>
                  ) : 
                  (
                    <h1 
                    className='w-full text-center p-2 text-primary text-xl lg:text-[42px] lg:leading-[50px] font-bold lg:my-4'
                    dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}
                    >
                    <p className="block mx-auto max-w-[400px] lg:max-w-[500px]">
                      {(currentView === 'login') ? 
                      `${tCommon('loginHeading')}` 
                      : (currentView === 'guest' && name === '') ?
                      `${tCommon('guestHeading')}`
                      : (currentView === 'convert') ? 
                      `${tCommon('convertHeading')}`
                      :
                      `${tCommon('registerHeading')}`
                      }
                    </p>
                  </h1>
                  )
                  }
                  <div className={`w-full items-center justify-center ${currentView == 'guest' ? '-mt-4' : ''}`}>
                      {(currentView === 'guest' || (currentView === 'login' && location != 'story')) && (
                        <div className='text-primary w-full flex items-center justify-center text-md font-medium text-center'>
                          <p className="max-w-[330px]">
                            {
                              currentView === 'guest' ? tCommon('guestDescription') : tCommon('loginDescription')
                            }
                          </p>
                        </div>
                      )}
                      <div className="w-full flex justify-center mt-1 lg:mt-4 px-2 lg:px-0">
                      <div
                        dir={locale === 'ar' ? "rtl" : "ltr"}
                        className="flex items-center gap-2 lg:gap-4 bg-[#E6E9ED] text-primary rounded-full px-3 lg:px-6 py-2 lg:text-sm font-light text-[11px]"
                      >
                        {/* 30 seconds */}
                        <div className={`flex items-center gap-1`}>
                          <span className="text-yellow-500">{currentView === 'guest' ? '⚡' : '🛒'}</span>
                          <span>{currentView === 'guest' ? tCommon('guestInfo1') : tCommon('RgisterInfo1')}</span>
                        </div>

                        <div className="h-5 w-px bg-gray-600" />

                        {/* No account */}
                        <div className={`flex items-center gap-1`}>
                          <span className="text-red-500">{currentView === 'guest' ? '🚫' : '📦'}</span>
                          <span>{currentView === 'guest' ? tCommon('guestInfo2') : tCommon('RegsterInfo2')}</span>
                        </div>

                        <div className="h-5 w-px bg-gray-600" />

                        {/* 100% secure */}
                        <div className={`flex items-center gap-1`}>
                          <span>{currentView === 'guest' ? '🔒' : '🎁'}</span>
                          <span>{currentView === 'guest' ? tCommon('guestInfo3') : tCommon('RegisterInfo3')}</span>
                        </div> 
                      </div>
                    </div>
                  </div>

                  {(currentView === 'register' || currentView === 'login') && location === 'story' && (
                    <div className='w-full items-center justify-center flex my-2' dir={locale == 'ar' ? 'rtl' : 'ltr'}>
                      <p className='font-medium text-sm text-black text-center  max-w-[330px] lg:max-w-full'>
                        {tCommon('registerDescription1')} 
                        <span 
                          className="text-primary font-bold underline underline-offset-2 underline-pimary cursor-pointer"
                          onClick={()=>{setCurrentView('guest')}}
                        >
                          {tCommon('registerDescription2')}
                        </span>
                        {tCommon('registerDescription3')}
                      </p>
                    </div>
                  )}
                  </>
                )
              }
                {
                  currentView === 'login' && 
                  <LogInView
                    currentView={currentView}
                    headingNumber={headingNumber}
                    verificationData={verificationData}
                    shouldVerify={shouldVerify}
                    setShouldVerify={setShouldVerify}
                    setIsOpen={setIsOpen}
                    setVerificationData={setVerificationData}
                    VerificationError={verificationError}
                    setVerificaitonError={setVerificationError}
                    handleVerification={handleVerification}
                    handleVerificationWhatsapp={handleVerificationWhatsapp}
                    setVerificationError={setVerificationError}
                    isOpen={isOpen}
                    setCurrentView={setCurrentView}
                    postLoginCallBack={postLoginCallback}
                    setPortLoginCallBack={setPostLoginCallback}
                    productType={productType}
                    setProductType={setProductType}
                    executePostLoginCallback={executePostLoginCallback}
                    onButtonClick={handleButtonClick}
                  />
                }
                {
                  currentView === 'register' &&
                  <RegisterView
                    currentView={currentView}
                    headingNumber={headingNumber}
                    verificationData={verificationData}
                    shouldVerify={shouldVerify}
                    setShouldVerify={setShouldVerify}
                    setIsOpen={setIsOpen}
                    setVerificationData={setVerificationData}
                    VerificationError={verificationError}
                    setVerificaitonError={setVerificationError}
                    setVerificationError={setVerificationError}
                    isOpen={isOpen}
                    setCurrentView={setCurrentView}
                    postLoginCallBack={postLoginCallback}
                    setPortLoginCallBack={setPostLoginCallback}
                    productType={productType}
                    setProductType={setProductType}
                    executePostLoginCallback={executePostLoginCallback}
                    onButtonClick={handleButtonClick}
                    showButton={showButton}
                    setShowButton={setShowButton}
                  />
                }
                {
                  currentView === 'forgot' && 
                  <ForgotView
                    currentView={currentView}
                    headingNumber={headingNumber}
                    verificationData={verificationData}
                    shouldVerify={shouldVerify}
                    setShouldVerify={setShouldVerify}
                    setIsOpen={setIsOpen}
                    setVerificationData={setVerificationData}
                    VerificationError={verificationError}
                    setVerificaitonError={setVerificationError}
                    setVerificationError={setVerificationError}
                    isOpen={isOpen}
                    setCurrentView={setCurrentView}
                    handleVerification={handleVerification}
                    postLoginCallBack={postLoginCallback}
                    setPostLoginCallBack={setPostLoginCallback}
                    executePostLoginCallback={executePostLoginCallback}
                    setProductType={setProductType}
                    onButtonClick={handleButtonClick}
                  />
                }
                {
                  currentView === 'guest' && 
                  <GuestView
                    currentView={currentView}
                    headingNumber={headingNumber}
                    verificationData={verificationData}
                    shouldVerify={shouldVerify}
                    setShouldVerify={setShouldVerify}
                    setIsOpen={setIsOpen}
                    setVerificationData={setVerificationData}
                    VerificationError={verificationError}
                    setVerificaitonError={setVerificationError}
                    setVerificationError={setVerificationError}
                    isOpen={isOpen}
                    setCurrentView={setCurrentView}
                    postLoginCallBack={postLoginCallback}
                    setPortLoginCallBack={setPostLoginCallback}
                    productType={productType}
                    setProductType={setProductType}
                    executePostLoginCallback={executePostLoginCallback}
                    onButtonClick={handleButtonClick}
                    name={name}
                  />
                }
                {currentView === 'convert' && (
                  <ConvertView
                    currentView={currentView}
                    headingNumber={headingNumber}
                    verificationData={verificationData}
                    shouldVerify={shouldVerify}
                    setShouldVerify={setShouldVerify}
                    setIsOpen={setIsOpen}
                    setVerificationData={setVerificationData}
                    VerificationError={verificationError}
                    setVerificaitonError={setVerificationError}
                    setVerificationError={setVerificationError}
                    isOpen={isOpen}
                    setCurrentView={setCurrentView}
                    postLoginCallBack={postLoginCallback}
                    setPortLoginCallBack={setPostLoginCallback}
                    productType={productType}
                    setProductType={setProductType}
                    executePostLoginCallback={executePostLoginCallback}
                    onButtonClick={handleButtonClick}
                  />
                )}

                <div className="w-full items-center justify-center">
                      <div className="w-full flex justify-center my-2">
                      <div
                        dir={locale == 'ar' ? "rtl" : "ltr"}
                        className="flex items-center gap-1 lg:gap-4 text-primary rounded-full px-6 py-2 text-[7px] lg:text-[10px] font-normal"
                      >
                        {/* 30 seconds */}
                        <div className="flex items-center gap-1">
                          <span>{tCommon('guestSubInfo1')}</span>
                        </div>

                        <div className="h-5 w-px bg-gray-600" />

                        {/* No account */}
                        <div className="flex items-center gap-1">
                          <span>{tCommon('guestSubInfo2')}</span>
                        </div>

                        <div className="h-5 w-px bg-gray-600" />

                        {/* 100% secure */}
                        <div className="flex items-center gap-1">
                          <span>{tCommon('guestSubInfo3')}</span>
                        </div>
                      </div>
                    </div>
                    </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
        </div>
      </Dialog>
    </Transition>
  )
}